import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable, makeObservable } from 'mobx';
import { PropTypes } from 'prop-types';
import { uploadStore } from '../../core/stores/upload.store';

@observer
export default class Dropzone extends React.Component {

    static propTypes = {
        id: PropTypes.string.isRequired,
        disabled: PropTypes.bool,
        existingFile: PropTypes.object,
        isRequired: PropTypes.bool,
        labelText: PropTypes.string,
        helperText: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.object
        ]),
        allowedExtensions: PropTypes.array,
        contentType: PropTypes.string,
        isImageUpload: PropTypes.bool,
        onChange: PropTypes.func,
        hideDropArea: PropTypes.bool
    }

    static defaultProps = {
        disabled: false,
        existingFile: {},
        isRequired: true,
        labelText: 'Upload',
        isImageUpload: false,
        onChange: () => {},
        hideDropArea: false
    }

    constructor(props) {
        super(props);
        makeObservable(this);
        this.isRequired = this.props.isRequired;
        this.existingFile = this.props.existingFile;
        this.fileMetaObject = uploadStore.registerDropzone({
            id: this.props.id,
            allowedExtensions: this.props.allowedExtensions,
            contentType: this.props.contentType,
            filename: this.props.existingFile.filename,
            uploadDate: this.props.existingFile.uploadDate
        });
    }

    componentDidMount() {
        // Reset any errors on a new mount, just in case.
        this.fileMetaObject.invalidUploadAttempted = []
    }

    componentDidUpdate(prevProps) {
        if (prevProps.isRequired !== this.props.isRequired) {
            this.isRequired = this.props.isRequired;
        }
        if (prevProps.existingFile !== this.props.existingFile) {
            this.existingFile = this.props.existingFile;
        }
    }

    componentWillUnmount() {
        uploadStore.deRegisterDropzone(this.props.id);
    }

    @observable isDraggedOver = false;
    @observable isDirty = false;
    @observable isRequired;
    @observable existingFile;

    @computed get hasError() {
      return this.missingError || this.invalidError;
    }

    @computed get missingError() {
      return this.isRequired && this.isDirty && !this.existingFile;
    }

    @computed get invalidError() {
      return this.fileMetaObject.invalidUploadAttempted.length > 0;
    }

    @computed get dropzoneClass() {
        let className = '';
        if (this.hasError) {
            className += 'has-error';
        }
        if (this.fileMetaObject.filename) {
            className += ' uploaded';
            return className;
        } else if (this.isDraggedOver) {
            className += ' is-drag-over';
            return className;
        }
        return className;
    }

    onDragEnter = (event) => {
        event.preventDefault();
        this.isDraggedOver = true;
    }

    onDragLeave = (event) => {
        event.preventDefault();
        this.isDraggedOver = false;
    }

    handleUpload = (event) => {
        event.preventDefault();
        this.isDirty = true;

        //if the upload was triggered during a drag event, remove the isDragged Over
        if (event.target.type !== 'file') {
          this.isDraggedOver = false;
        }
        const promise = this.props.isImageUpload
          ? uploadStore.handleImageUpload(event, this.props.id)
          : uploadStore.handleFileUpload(event, this.props.id);
        promise.then((res) => this.props.onChange(res.url || res.name))
        this.primaryButtonRef.focus();
    }

    handleRemoveFile = () => {
      this.isDirty = true;
        uploadStore.handleRemoveFile(this.props.id);
        this.props.onChange(null);
        this.primaryButtonRef.focus();
    }

    handlePrimaryButtonClick = () => {
        if (!this.props.disabled) {
            this.fileBrowseInputEl.click();
        }
    }

    renderLabelSection = () => {
        return (
          <div id="dropzone-label" className={`control-label ${this.hasError ? 'error' : ''}`}>
              <span className="sr-only">File upload area:</span>
              <span className={this.props.isRequired ? 'required' : ''}>
                {this.props.labelText}
              </span>
              <span className="help-text as-block">
                  {this.props.helperText}
              </span>
              {this.renderErrorSection(!this.fileMetaObject.isValid)}
          </div>
        )
    }

    renderErrorSection = (announceImmediately = false) => {
      let errorText;
      if(this.invalidError) {
        errorText = this.fileMetaObject.invalidUploadAttempted.join('. ')
      } else if(this.missingError) {
        errorText = 'Please upload a file.';
      }
        return (<div className="msgBlock error error-list">
            <span role={announceImmediately ? 'status' : ''} aria-live={announceImmediately ? 'assertive' : ''} aria-atomic="true">
                {errorText}
            </span>
        </div>)
    }

    renderStatusBar = () => {
        return (
            <React.Fragment>
                {this.fileMetaObject.isUploading &&
                    <div className="progress loading-indicator">
                        <div className="progress-bar progress-bar-striped active" role="progressbar" aria-valuenow={this.fileMetaObject.progress} aria-valuemin="0" aria-valuemax="100" style={{ width: `${this.fileMetaObject.progress}%` }}>
                            <span className="sr-only">{this.fileMetaObject.progress}% Complete</span>
                        </div>
                    </div>
                }
                {this.fileMetaObject.isDeleting &&
                    <div className="progress loading-indicator">
                        <span className="sr-only">Deleting certifications file</span>
                        <div className="progress-bar progress-bar-striped progress-bar-danger active" role="progressbar" style={{ width: '100%' }}></div>
                    </div>
                }
            </React.Fragment>
        );
    }

    renderFileName = () => {
        const displayName = this.fileMetaObject.filename;
        return (
            <React.Fragment>
                <div className="dropzone-icon"><i aria-hidden className="icon icon-file dkgrey"></i></div>
                <div className="dropzone-file-info">
                    <div className="file-name">
                        {displayName}
                    </div>
                    {/* FPSE-3175 - removed timestamp for uploaded files because it isn't provided by the server. Requested by Aygyun */}
                    <small>{!this.fileMetaObject.filename ? 'Uploading...' : ''}</small>
                    </div>
            </React.Fragment>
        )
    }

    renderDragAndDropInstructions = () => {
        return (
            <React.Fragment>
                <div><i aria-hidden className="icon icon-upload dkgrey"></i></div>
                <p><strong>Drag and Drop File Here</strong></p>
                <p>or</p>
            </React.Fragment>
        );
    }

    renderInstructionArea = () => {
        const displayName = this.fileMetaObject.filename;
        return (
            <div className="dropzone-content">
                {displayName &&
                    this.renderFileName()
                }
                {!displayName && !this.fileMetaObject.isDeleting &&
                    this.renderDragAndDropInstructions()
                }
            </div>
        )
    }

    renderInputAndButton = () => {
        const containsFile = Boolean(this.fileMetaObject.filename);
        const onClick = containsFile
            ? this.handleRemoveFile
            : this.handlePrimaryButtonClick;
        const isDisabled = this.fileMetaObject.isUploading || this.fileMetaObject.isDeleting || this.props.disabled;
        const buttonLabel = containsFile
            ? (<span>Remove <span className="sr-only">previously uploaded</span> File</span>)
            : 'Browse Files';
        return (<div className="file-input">
            <button
              aria-labelledby={!containsFile ? 'dropzone-label' : ''}
              ref={i => this.primaryButtonRef = i}
              onClick={onClick}
              disabled={isDisabled}
              className="fn-primary"
              type="button">
                {buttonLabel}
                {!containsFile && <input
                  ref={i => this.fileBrowseInputEl = i}
                  aria-labelledby="dropzone-label"
                  type="file"
                  onChange={this.handleUpload}
                  value="" />
              }
            </button>
        </div>);
    }

    render() {
        return (
            <div className="dropzone-wrapper form-group">
                {this.renderLabelSection()}
                {!this.props.hideDropArea && <div
                  className={`dropzone ${this.dropzoneClass}`}
                  onDragLeave={this.onDragLeave}
                  onDragEnter={this.onDragEnter}
                  onDrop={this.handleUpload}
                  onDragOver={(event) => event.preventDefault()}>
                    {(this.fileMetaObject.isUploading || this.fileMetaObject.isDeleting) &&
                        this.renderStatusBar()
                    }
                    {this.renderInstructionArea()}
                    {this.renderInputAndButton()}
                </div>}
            </div>
        );
    }
}
